import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Page } from "react-page-layout";
import { localize } from "redux-i18n";
import { compose } from "redux";
import classNames from "classnames";
import moment from "moment";
import html2canvas from "html2canvas";
import { MEDICAL } from "@modules/shared/constants/employee-absences";
import { transformSecondsInHours } from "@transformers/date";
// TYPES
import type { EmployeeType, EmployeeActionSummaryType } from "@modules/employee/types";
import type { State } from "@store/reducers";
import type { RoleSummary } from "@modules/employee/reducer/employment-details/types";
import type {
  EmployeeAbsence,
  AbsenceStatistics,
} from "@modules/shared/reducer/employee-absences/types";
import type { EffectiveUserPermissions } from "@modules/service/reducer/user-permissions/types";

// ACTIONS
import actionCreators from "@modules/reporting/actionCreator";
import sharedActionCreators from "@modules/shared/actionCreator";
import employeeActionCreators from "@modules/employee/actionCreator";

// SELECTORS
import { getRouteParams } from "@selectors";
import { isFirstCareUser } from "@modules/account/selectors";
import { getDetails } from "@modules/reporting/selectors/employee/employee-details";
import {
  getEmployeePositions,
  getEmployeePositionsPagination,
} from "@modules/shared/selectors/employee-positions";
import { getActionSummary } from "@modules/employee/selectors";
import { getAbsences, getAbsenceStatistics } from "@modules/shared/selectors/employee-absences";
import { getEffectiveUserPermissions } from "@modules/service/selectors/user-permissions";
import { getEventHistoryList } from "@modules/shared/selectors";
import { isLoading } from "@modules/shared/selectors";

// COMPONENTS
import { EmploymentSummary } from "@modules/shared/components";
import withErrorHandler from "@components/error";
import { withClientContext, type PropsFromClientContext } from "@modules/context/components";
import {
  withPermissions,
  PermissionEnum,
  type PropsFromPermissions,
} from "@modules/account/components";
import { TitleSection, Button, View, Loader, Text, StatItem, GSGrid } from "@components";
import {
  ExportPersonalDetails,
  ExportEmploymentSummary,
  ExportRoleSummary,
  ExportAbsenceHistory,
} from "@modules/reporting/components/export-employee-report";
import type { EventHistoryItem } from "@modules/shared/reducer/employee-events/types";
import { formatDate } from "@transformers/date";
import { formatAuthoredBy } from "@transformers/names";
import { ActionSummary } from "@modules/employee/components";
import {
  AbsenceStatisticTable,
  AbsenceStatisticWeekday,
  AbsenceStatisticPerReason,
  AbsenceStatisticReportingCompliance,
  AbsenceStatisticFitNoteCompliance,
  AbsenceStatisticClosingCompliance,
  AbsenceStatisticPerEmployee,
} from "@modules/shared/components";

// HELPERS

type PropsFromState = $Exact<{
  isFirstCareUser: boolean,
  isLoading: boolean,
  employee: ?EmployeeType,
  employeePositions: Array<RoleSummary>,
  actionSummary?: Array<EmployeeActionSummaryType>,
  absences: Array<EmployeeAbsence>,
  absenceStatistics: ?AbsenceStatistics,
  effectiveUserPermissions: ?EffectiveUserPermissions,
  eventHistoryList: Object,
  employeePagination: Object,
}>;

type PropsFromDispatch = $Exact<{
  getEmployeeDetails: typeof actionCreators.GetEmployeeDetails,
  getEmployeePositions: typeof sharedActionCreators.GetSharedEmployeePositions,
  getActionSummary: typeof employeeActionCreators.GetEmployeeActionSummary,
  getEmployeeAbsences: typeof sharedActionCreators.GetEmployeeAbsences,
  getEmployeeAbsenceStatistics: typeof sharedActionCreators.GetAbsenceStatistics,
  getEventHistory: typeof sharedActionCreators.GetEmployeeEventHistory,
}>;

type Props = $Exact<{
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
  ...PropsFromClientContext,
  ...PropsFromPermissions,
  employeeId: number,
}>;

export function EmployeeExport(props: Props) {
  const columnsConfig = [
    {
      title: props.t("ACTIVE_ROLES"),
      value: props.employee?.employmentSummary.totalNumberOfActivePositions,
    },
    {
      title: props.t("WEEKLY_CONTRACTED_HOURS"),
      value: transformSecondsInHours(
        props.employee?.employmentSummary.totalWeeklyContractedHoursInSeconds,
      ),
    },
    {
      title: props.t("WORKING_DAYS_YEAR"),
      value: props.employee?.employmentSummary.totalWorkingDaysPerYear,
    },
    {
      title: props.t("TOTAL_FTE"),
      value: props.employee?.employmentSummary.totalFte,
    },
  ];
  const eventHistoryData = props.eventHistoryList?.ALL?.content || [];

  useEffect(() => {
    props.getEmployeeDetails({
      clientId: props.clientId,
      employeeId: props.employeeId,
    });

    props.getEmployeePositions({
      clientId: props.clientId,
      employeeId: props.employeeId,
    });

    props.getActionSummary({
      clientId: props.clientId,
      employeeId: props.employeeId,
    });

    props.getEmployeeAbsences({
      clientId: props.clientId,
      employeeId: props.employeeId,
      isReportingRequest: true,
      limit: 2000,
    });

    props.getEmployeeAbsenceStatistics({
      clientId: props.clientId,
      employeeId: props.employeeId,
      filters: {
        toDate: moment().format("YYYY-MM-DD"),
        fromDate: moment()
          .subtract(1, "years")
          .format("YYYY-MM-DD"),
        absenceType: MEDICAL,
      },
    });

    props.getEventHistory({
      clientId: props.clientId,
      employeeId: props.employeeId,
      limit: 2000,
    });
  }, []);

  function changePage(page: number) {
    props.getEmployeePositions({
      clientId: props.clientId,
      employeeId: props.employeeId,
      page,
    });
  }

  function printHistoryData() {
    const data = props.absences || [];
    const result = [];
    const rowsPerPage = 32; // page height (1734) / row height (40) and minus a bit more to create small offset + minus history header

    for (let i = 0; i < data.length; i += rowsPerPage) {
      const singleGridData = data.slice(i, i + rowsPerPage);
      result.push(singleGridData);
    }

    return result;
  }

  function handleExport() {
    const printArea = document.querySelector("#print-area");
    const dontPrint = document.querySelectorAll(
      '.sidebar, [data-name="AppHeader"],.tab-bar .heading,.tab-bar .tabs',
    );
    if (dontPrint.length) {
      printArea.classList.add("print-width");
      dontPrint.forEach(el => {
        el.classList.add("d-print-none");
      });
      setTimeout(() => {
        window.print();
        dontPrint.forEach(el => {
          el.classList.remove("d-print-none");
        });
        printArea.classList.remove("print-width");
      }, 100);
    }
  }

  if (!props.employee) {
    return null;
  }
  const { employmentSummary } = props.employee;
  const name = `${props.employee?.firstName} ${props.employee?.lastName}`;
  return (
    <View position="relative" minHeight="50vh">
      {props.isLoading && <Loader inline />}
      {!props.isLoading && (
        <View id="print-area">
          <View className="a4-page" gap={4}>
            {/* Header End */}

            {/* Employee Details */}
            {employmentSummary && (
              <View>
                <View direction="row">
                  <View flex="1" direction="row" alignItems="center">
                    <Text type="h5" text={props.t("EMPLOYEE_DETAILS")} />
                  </View>
                  <Button
                    materialIcon="file_download"
                    className="primary d-print-none"
                    onClick={handleExport}
                  >
                    {props.t("EXPORT")}
                  </Button>
                </View>
                <View gap={4}>
                  <EmploymentSummary summary={employmentSummary} customItems={columnsConfig} />
                  {/* Personal Details */}
                  <ExportPersonalDetails employee={props.employee || {}} />
                  {/* Role Details */}
                  <ExportRoleSummary
                    roleSummary={props.employeePositions || []}
                    pagination={props.employeePagination}
                    onChange={changePage}
                  />
                </View>
              </View>
            )}
            {/* Employee Details END  */}
          </View>
          {/* Absence History */}
          <View className="a4-page " gap={8}>
            <Text type="h4" text={props.t("ABSENCE_HISTORY")} />
            <View gap={4} className="">
              {/* Summary */}
              {props?.actionSummary?.values && (
                <View gap={3}>
                  <Text type="h5" text={props.t("SUMMARY")} />
                  <View direction="row">
                    {props.actionSummary?.values.map(item => {
                      return (
                        <StatItem
                          key={item.description}
                          title={item.description}
                          value={item.value}
                        />
                      );
                    })}
                  </View>
                </View>
              )}
              {
                <View gap={8}>
                  {/* History */}
                  <ExportAbsenceHistory className="d-print-none" absences={props.absences || []} />
                  <ExportAbsenceHistory
                    className="d-print-only"
                    absences={(props.absences || []).slice(0, 30)}
                  />
                </View>
              }
            </View>
          </View>

          {props.absences.length > 30 && (
            <View className="a4-page d-print-only" gap={8}>
              {/* History */}
              <ExportAbsenceHistory absences={(props.absences || []).slice(30, 60) || []} />
            </View>
          )}

          {/* Absence History */}
          {props.absences.length > 60 && (
            <View className="a4-page d-print-only" gap={8}>
              {/* History */}
              <ExportAbsenceHistory absences={(props.absences || []).slice(60) || []} />
            </View>
          )}

          {/* Absence History END */}
          {/* Absence Statistics */}
          {props.absenceStatistics && (
            <View className="a4-page" gap={4}>
              <Text type="h4" text={props.t("ABSENCE_STATISTICS")} />
              <View gap={4}>
                <AbsenceStatisticTable
                  data={props.absenceStatistics || []}
                  effectiveUserPermissions={props.effectiveUserPermissions}
                  showAdditionalColumns
                />
              </View>
              <View direction="row" gap={4}>
                {/* Total Days Lost (By Weekday) */}
                <AbsenceStatisticWeekday data={props.absenceStatistics || []} />

                {/* Days Lost Per Reason */}
                <AbsenceStatisticPerReason data={props.absenceStatistics || []} />
              </View>

              {/* Absence Reporting Compliance          Fit Note Compliance*/}
              <View direction="row" gap={4}>
                <AbsenceStatisticReportingCompliance data={props.absenceStatistics || []} />
                <AbsenceStatisticFitNoteCompliance data={props.absenceStatistics || []} />
              </View>
            </View>
          )}
          {props.absenceStatistics && (
            <View className="a4-page" gap={4}>
              <Text type="h4" text={props.t("ABSENCE_STATISTICS")} />

              {/* Absence Closing Compliance            Days Lost Per Employee*/}
              <View direction="row" gap={4}>
                <AbsenceStatisticClosingCompliance data={props.absenceStatistics || []} />
                <AbsenceStatisticPerEmployee data={props.absenceStatistics || []} />
              </View>
            </View>
          )}
          {/* Absence Statistics END */}
          <View className="a4-page d-print-none" gap={4}>
            <Text type="h4" text={props.t("ACTIVITY_HISTORY")} />
            <GSGrid
              maxHeight="auto"
              columns={[
                {
                  key: "id",
                  name: props.t("ACTIVITY_ID"),
                },
                {
                  key: "taskType",
                  name: props.t("TASK_TYPE"),
                  renderCell: ({ row }: { row: EventHistoryItem }) => props.t(row.taskType),
                  align: "left",
                },
                {
                  key: "description",
                  name: props.t("DESCRIPTION"),
                  align: "left",
                  width: 640,
                  renderCell: ({ row }: { row: EventHistoryItem }) =>
                    GSGrid.render.ellipsis(row.description),
                },
                {
                  key: "createdAt",
                  name: props.t("ACTIVITY_DATE"),
                  renderCell: ({ row }: { row: Object }) => formatDate(row.createdAt),
                },
                {
                  key: "createdBy",
                  name: props.t("ACTIONED_BY"),
                  renderCell: ({ row }: { row: EventHistoryItem }) =>
                    formatAuthoredBy(row.createdBy),
                  align: "left",
                },
              ]}
              rows={eventHistoryData}
            />
          </View>

          {eventHistoryData.length && (
            <View className="a4-page d-print-only" gap={4}>
              <Text type="h4" text={props.t("ACTIVITY_HISTORY")} />
              <GSGrid
                maxHeight="auto"
                columns={[
                  {
                    key: "id",
                    name: props.t("ACTIVITY_ID"),
                  },
                  {
                    key: "taskType",
                    name: props.t("TASK_TYPE"),
                    renderCell: ({ row }: { row: EventHistoryItem }) => props.t(row.taskType),
                    align: "left",
                  },
                  {
                    key: "description",
                    name: props.t("DESCRIPTION"),
                    align: "left",
                    width: 640,
                    renderCell: ({ row }: { row: EventHistoryItem }) =>
                      GSGrid.render.ellipsis(row.description),
                  },
                  {
                    key: "createdAt",
                    name: props.t("ACTIVITY_DATE"),
                    renderCell: ({ row }: { row: Object }) => formatDate(row.createdAt),
                  },
                  {
                    key: "createdBy",
                    name: props.t("ACTIONED_BY"),
                    renderCell: ({ row }: { row: EventHistoryItem }) =>
                      formatAuthoredBy(row.createdBy),
                    align: "left",
                  },
                ]}
                rows={eventHistoryData.slice(0, 36)}
              />
            </View>
          )}
          {eventHistoryData.length > 36 && (
            <View className="a4-page d-print-only" gap={4}>
              <Text type="h4" text={props.t("ACTIVITY_HISTORY")} />
              <GSGrid
                maxHeight="auto"
                columns={[
                  {
                    key: "id",
                    name: props.t("ACTIVITY_ID"),
                  },
                  {
                    key: "taskType",
                    name: props.t("TASK_TYPE"),
                    renderCell: ({ row }: { row: EventHistoryItem }) => props.t(row.taskType),
                    align: "left",
                  },
                  {
                    key: "description",
                    name: props.t("DESCRIPTION"),
                    align: "left",
                    width: 640,
                    renderCell: ({ row }: { row: EventHistoryItem }) =>
                      GSGrid.render.ellipsis(row.description),
                  },
                  {
                    key: "createdAt",
                    name: props.t("ACTIVITY_DATE"),
                    renderCell: ({ row }: { row: Object }) => formatDate(row.createdAt),
                  },
                  {
                    key: "createdBy",
                    name: props.t("ACTIONED_BY"),
                    renderCell: ({ row }: { row: EventHistoryItem }) =>
                      formatAuthoredBy(row.createdBy),
                    align: "left",
                  },
                ]}
                rows={eventHistoryData.slice(36, 72)}
              />
            </View>
          )}
          {eventHistoryData.length > 72 && (
            <View className="a4-page d-print-only" gap={4}>
              <Text type="h4" text={props.t("ACTIVITY_HISTORY")} />
              <GSGrid
                maxHeight="auto"
                columns={[
                  {
                    key: "id",
                    name: props.t("ACTIVITY_ID"),
                  },
                  {
                    key: "taskType",
                    name: props.t("TASK_TYPE"),
                    renderCell: ({ row }: { row: EventHistoryItem }) => props.t(row.taskType),
                    align: "left",
                  },
                  {
                    key: "description",
                    name: props.t("DESCRIPTION"),
                    align: "left",
                    width: 640,
                    renderCell: ({ row }: { row: EventHistoryItem }) =>
                      GSGrid.render.ellipsis(row.description),
                  },
                  {
                    key: "createdAt",
                    name: props.t("ACTIVITY_DATE"),
                    renderCell: ({ row }: { row: Object }) => formatDate(row.createdAt),
                  },
                  {
                    key: "createdBy",
                    name: props.t("ACTIONED_BY"),
                    renderCell: ({ row }: { row: EventHistoryItem }) =>
                      formatAuthoredBy(row.createdBy),
                    align: "left",
                  },
                ]}
                rows={eventHistoryData.slice(72)}
              />
            </View>
          )}
        </View>
      )}
    </View>
  );
}

const mapStateToProps = (state: State, ownProps): PropsFromState => {
  const routeParams = getRouteParams(ownProps);

  return {
    isLoading: isLoading(state),
    isFirstCareUser: isFirstCareUser(state),
    employee: getDetails(state),
    employeePositions: getEmployeePositions(state),
    employeePagination: getEmployeePositionsPagination(state),
    actionSummary: getActionSummary(state),
    absences: getAbsences(state),
    absenceStatistics: getAbsenceStatistics(state),
    effectiveUserPermissions: getEffectiveUserPermissions(state),
    eventHistoryList: getEventHistoryList(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  getEmployeeDetails: actionCreators.GetEmployeeDetails,
  getEmployeePositions: sharedActionCreators.GetSharedEmployeePositions,
  getActionSummary: employeeActionCreators.GetEmployeeActionSummary,
  getEmployeeAbsences: sharedActionCreators.GetEmployeeAbsences,
  getEmployeeAbsenceStatistics: sharedActionCreators.GetAbsenceStatistics,
  getEventHistory: sharedActionCreators.GetEmployeeEventHistory,
};

export default compose(
  withErrorHandler(),
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(EmployeeExport);
